import { Alert, Card, Spinner } from "flowbite-react";
import ReactApexChart from "react-apexcharts";
import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import axios from "../../config/axiosInstance";
import { ADMIN } from "../../config/apiConfig";

const ModularResponseTimeChart = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const {
    data: rawData,
    isLoading,
    error,
  } = useQuery(
    ["modularResponseTimes", startDate, endDate],
    async () => {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const { data } = await axios.get(ADMIN.GET_MODULAR_RESPONSE_TIMES, {
        params,
      });
      return data?.data ?? [];
    },
    {
      keepPreviousData: true,
    }
  );

  const chartData = useMemo(() => {
    if (!Array.isArray(rawData)) return [];
    return rawData
      .map((item) => ({
        ...item,
        avgExecutionTime: Number(item.avgExecutionTime) || 0,
      }))
      .sort((a, b) => b.avgExecutionTime - a.avgExecutionTime)
      .slice(0, 12);
  }, [rawData]);

  const categories = useMemo(
    () =>
      chartData.map(
        (item) =>
          item.queryName ||
          (item.QueryId ? `Consulta #${item.QueryId}` : "Consulta modular")
      ),
    [chartData]
  );

  const series = useMemo(
    () => [
      {
        name: "Tempo médio (s)",
        data: chartData.map((item) => Number(item.avgExecutionTime || 0)),
      },
    ],
    [chartData]
  );

  const options = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        animations: { enabled: true },
      },
      plotOptions: {
        bar: {
          borderRadius: 6,
          dataLabels: {
            position: "top",
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${Number(val).toFixed(2)}s`,
        style: { fontSize: "10px", colors: ["#0f172a"] },
      },
      xaxis: {
        categories,
        labels: {
          rotate: -45,
          style: { fontSize: "11px" },
        },
      },
      yaxis: {
        labels: {
          formatter: (value) => `${Number(value).toFixed(2)}s`,
        },
        title: {
          text: "Segundos",
        },
      },
      tooltip: {
        y: {
          formatter: (value) => `${Number(value).toFixed(2)}s`,
        },
      },
      grid: {
        borderColor: "#e5e7eb",
      },
    }),
    [categories]
  );

  const hasFilters = startDate || endDate;

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              Tempo médio de resposta das consultas modulares
            </h3>
            <span className="text-xs text-gray-500">
              baseado apenas em consultas com sucesso
            </span>
          </div>
          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setStartDate("");
                setEndDate("");
              }}
              className="text-xs text-blue-600 hover:underline"
            >
              Limpar filtro
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-3 text-sm text-gray-700">
          <label className="flex flex-col gap-1">
            <span className="font-medium">Data inicial</span>
            <input
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              className="h-9 w-40 rounded border border-gray-300 px-3 text-sm outline-none focus:border-blue-500"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-medium">Data final</span>
            <input
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              className="h-9 w-40 rounded border border-gray-300 px-3 text-sm outline-none focus:border-blue-500"
            />
          </label>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <Alert color="failure">
            <span className="text-sm">
              Ocorreu um erro ao buscar os tempos de resposta.
            </span>
          </Alert>
        ) : !chartData.length ? (
          <p className="text-sm text-gray-500">
            Ainda não existem consultas modulares com tempo registrado.
          </p>
        ) : (
          <div className="h-[380px]">
            <ReactApexChart
              options={options}
              series={series}
              type="bar"
              height={360}
            />
          </div>
        )}
      </div>
    </Card>
  );
};

export default ModularResponseTimeChart;
